import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { useState, useMemo, useCallback } from 'react';
import { useRequest } from 'ahooks';
import { Button, FormBuilder, Space, Badge } from 'udesk-ui';
import ImageViewer from 'src/components/common/ImageViewer';
import { getLabel, useOptions } from 'src/pages/conversationAnalysis/hooks';
import Page from 'Component/common/udesk-qa-page-layout';
import CommonTablePage from 'Component/common/udesk-qa-table-page';
import type { TableComponentProps } from 'Component/common/udesk-qa-table-page';
import type { FormProps } from 'udesk-ui/lib/form-builder/types/form';
import { getProductDetails } from 'src/api/productDetails';
import { GetProductDetailsQuery, ProductDetailFoundResponse } from 'src/api/types';
import isNil from 'lodash/isNil';

interface FormModel extends Pick<GetProductDetailsQuery, 'channelSource' | 'title'> {}

const Template = React.memo((props: any) => {
    // const { sdkOptions } = props;

    const locales = UdeskLocales['current'];

    const [initialValues] = useState<FormModel>({
        channelSource: undefined,
        title: undefined,
    });

    const [searchData, setSearchData] = useState<FormModel>(initialValues);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [dataSource, setDataSource] = useState<ProductDetailFoundResponse[]>([]);

    const { run: getList, loading } = useRequest(
        (params: GetProductDetailsQuery) =>
            getProductDetails({
                params,
            }),
        {
            onSuccess: (res) => {
                setDataSource(res.data || []);
                setPageNum(res.paging?.pageNum ?? 0);
                setPageSize(res.paging?.pageSize ?? 0);
                setTotal(res.paging?.total ?? 0);
            },
        }
    );

    const onFinish = useCallback(
        (values: FormModel) => {
            setSearchData(values);
            getList(values);
        },
        [getList]
    );

    const [form] = FormBuilder.useForm();

    const { ECommerceTypeOptions } = useOptions();

    const config: FormProps = {
        initialValues: initialValues,
        fields: [
            {
                name: 'channelSource',
                label: /* 电商平台 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productDetail.list.index.ecommercePlatform,
                type: 'Select',
                props: {
                    style: {
                        width: 200,
                    },
                    placeholder:
                        /* 请选择 */ UdeskLocales['current'].pages.intelligentAnalysis.ruleTemplate
                            .index.pleaseSelect,
                    allowClear: true,
                    options: ECommerceTypeOptions,
                },
            },
            {
                name: 'title',
                label: /* 产品标题 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productDetail.list.index.productTitle,
                type: 'Input',
                props: {
                    placeholder: locales.labels.pleaseEnter,
                },
            },
        ],
        layout: 'inline',
        footerRender: () => (
            <Button onClick={() => form?.submit?.()} type="primary">
                {locales.labels.query}
            </Button>
        ),
        onFinish,
    };

    const columns = useMemo<any[]>(() => {
        return [
            {
                key: 'image',
                dataIndex: 'image',
                title: /* 产品图片 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productDetail.list.index.productPicture,
                width: '10%',
                render: (v, item) => {
                    return v ? (
                        <ImageViewer>
                            <img width={80} src={v} alt="" />
                        </ImageViewer>
                    ) : (
                        '-'
                    );
                },
            },
            {
                key: 'code',
                dataIndex: 'code',
                title: /* 产品ID */ UdeskLocales['current'].pages.conversationAnalysis.productDetail
                    .list.index.productID,
                width: '10%',
            },
            {
                key: 'title',
                dataIndex: 'title',
                title: /* 产品标题 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productDetail.list.index.productTitle,
                width: '10%',
            },
            {
                key: 'channelSource',
                dataIndex: 'channelSource',
                title: /* 电商平台 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productDetail.list.index.ecommercePlatform,
                width: '10%',
                render: (v) => {
                    return getLabel(ECommerceTypeOptions, v);
                },
            },
            {
                key: 'price',
                dataIndex: 'price',
                title: /* 价格 */ UdeskLocales['current'].pages.conversationAnalysis.productDetail
                    .list.index.price,
                width: '10%',
                render: (v, item) => {
                    return v + item.priceUnit;
                },
            },
            {
                key: 'sales',
                dataIndex: 'sales',
                title: /* 销量 */ UdeskLocales['current'].pages.conversationAnalysis.productDetail
                    .list.index.sales,
                width: '10%',
                render: (value) => {
                    return value ?? '-';
                },
            },
            {
                key: 'commentCount',
                dataIndex: 'commentCount',
                title: /* 评论数量 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productDetail.list.index.numberOfComments,
                width: '10%',
            },
            {
                title: /* 关联状态 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productDetail.list.index.associationStatus,
                dataIndex: 'status',
                key: 'status',
                width: '10%',
                render: (_, record) => {
                    return !isNil(record.mainProductId) ? (
                        <Badge
                            status="success"
                            text={
                                /* 已关联 */ UdeskLocales['current'].pages.conversationAnalysis
                                    .productDetail.list.index.associated
                            }
                        />
                    ) : (
                        <Badge
                            status="default"
                            text={
                                /* 未关联 */ UdeskLocales['current'].pages.conversationAnalysis
                                    .productDetail.list.index.notAssociated
                            }
                        />
                    );
                },
            },
            {
                title: /* 关联主产品 */ UdeskLocales['current'].pages.conversationAnalysis
                    .productDetail.list.index.associatedMainProduct,
                dataIndex: 'mainProductName',
                key: 'mainProductName',
                width: '10%',
                render: (value, item) => {
                    const jump = (id) => {
                        if (id) {
                            window.open(
                                `/conversation-analysis/product-manage/detail/${id}/base-info`,
                                '_blank'
                            );
                        }
                    };

                    return (
                        <Button type="link" size="small" onClick={() => jump(item.mainProductId)}>
                            {value ?? '-'}
                        </Button>
                    );
                },
            },
            {
                title: /* 操作 */ UdeskLocales['current'].pages.intelligentAnalysis.ruleTemplate
                    .index.operation,
                dataIndex: 'actions',
                key: 'actions',
                align: 'center',
                render: (name, item, index) => {
                    return (
                        <Space>
                            <Button size="small" type="link" href={item.sourceUrl} target="_blank">
                                {/* 查看详情 */}
                                {
                                    UdeskLocales['current'].pages.conversationAnalysis.productDetail
                                        .list.index.viewDetails
                                }
                            </Button>
                        </Space>
                    );
                },
            },
        ];
    }, [ECommerceTypeOptions]);

    const tableProps = useMemo<TableComponentProps>(() => {
        return {
            columns,
            dataSource,
            cacheKey: false,
        };
    }, [columns, dataSource]);

    const paginationProps = useMemo(() => {
        return {
            current: pageNum,
            pageSize,
            total,
            onChange: (pageNum, pageSize) => {
                getList({
                    ...searchData,
                    pageNum,
                    pageSize,
                });
            },
        };
    }, [pageNum, pageSize, total, getList, searchData]);

    return (
        <Page
            pageBodyClassName="xxx-page-index"
            title={
                /* 产品详情 */ UdeskLocales['current'].pages.conversationAnalysis.productDetail.list
                    .index.productDetails
            }
            padding={true}
        >
            <CommonTablePage
                loading={loading}
                tableHeaderLeftRender={<FormBuilder {...config} form={form} />}
            >
                <CommonTablePage.Table {...tableProps}></CommonTablePage.Table>
                <CommonTablePage.Pagination {...paginationProps}></CommonTablePage.Pagination>
            </CommonTablePage>
        </Page>
    );
});

class Component extends React.Component {
    render() {
        return <Template {...this.props} />;
    }
}

export default Udesk.react.udeskify(Component);
